import React, { useEffect, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, LinearProgress, Link, MenuItem,
  Stack, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import billingApi, { BillingSummaryResponse, ClientCreditUsageResponse } from "../apis/billingApi";
import { formatCalendarDate } from "../utils/billingContract";
import { toDateTimeLocalInTimeZone } from "../shared/utils/timezoneUtils";

const credits = (units: number) => (Number(units) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });

export default function BillingClientUsage({ mspId, summary, refresh, onSelect }: {
  mspId: number; summary: BillingSummaryResponse; refresh: number;
  onSelect: (client: { id: string; name: string }, periodId: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [period, setPeriod] = useState("current");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [sort, setSort] = useState<"name" | "units">("units");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [result, setResult] = useState<ClientCreditUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const periodId = period === "current" ? String(summary.period?.id || "") : period;
  useEffect(() => {
    let live = true;
    if (!expanded) return;
    setLoading(true); setError("");
    const timer = setTimeout(() => {
      billingApi.getClientUsage(mspId, { period_id: periodId, search, offset: page * size, limit: size, sort, order })
        .then(value => {
          if (!live) return;
          if (page > 0 && page * size >= value.total) { setPage(0); return; }
          setResult(value);
        })
        .catch(e => { if (live) { setError(e instanceof Error ? e.message : "Could not load client usage"); setResult(null); } })
        .finally(() => { if (live) setLoading(false); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [mspId, periodId, search, page, size, sort, order, expanded, refresh, retry]);
  const changeSort = (value: "name" | "units") => {
    setOrder(sort === value ? order === "desc" ? "asc" : "desc" : value === "name" ? "asc" : "desc"); setSort(value); setPage(0);
  };
  const date = (value: string) => formatCalendarDate(toDateTimeLocalInTimeZone(value, summary.timezone).slice(0, 10), undefined, { month: "short", day: "numeric", year: "numeric" });
  return <Accordion expanded={expanded} onChange={(_, value) => setExpanded(value)} disableGutters variant="outlined">
    <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="client-usage-content" id="client-usage-heading">
      <Typography variant="h6">Usage by client</Typography>
    </AccordionSummary>
    <AccordionDetails id="client-usage-content">
      <Stack spacing={2}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <TextField size="small" label="Search clients" value={search} inputProps={{ maxLength: 200 }} sx={{ width: { xs: "100%", sm: 260 } }}
            onChange={e => { setSearch(e.target.value); setPage(0); }} />
          <TextField select size="small" label="Usage month" value={period} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ width: { xs: "100%", sm: 280 } }}
            onChange={e => { setPeriod(e.target.value); setPage(0); }}>
            <MenuItem value="current">{summary.period ? date(summary.period.starts_at) + " – " + date(summary.period.ends_at) : "Current month"}</MenuItem><MenuItem value="">All months</MenuItem>
            {summary.periods.filter(p => p.id !== summary.period?.id).map(p => <MenuItem key={p.id} value={String(p.id)}>{date(p.starts_at)} – {date(p.ends_at)}</MenuItem>)}
          </TextField>
        </Box>
        {error && <Alert severity="error" action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}>{error}</Alert>}
        {loading ? <LinearProgress aria-label="Loading client usage" /> : result && <Typography variant="body2" color="text.secondary">
          {result.total.toLocaleString()} {result.total === 1 ? "client" : "clients"} · {credits(result.units)} credits used
        </Typography>}
        <Box sx={{ overflowX: "auto" }}><Table size="small" aria-label="Usage by client" sx={{ minWidth: 740 }}>
          <TableHead><TableRow>
            <TableCell sortDirection={sort === "name" ? order : false}><TableSortLabel active={sort === "name"} direction={sort === "name" ? order : "asc"} onClick={() => changeSort("name")}>Client</TableSortLabel></TableCell>
            {["Chat Assistant", "Automatic tickets", "Technician tickets", "Workflows"].map(label => <TableCell key={label} align="right">{label}</TableCell>)}
            <TableCell align="right" sortDirection={sort === "units" ? order : false}><TableSortLabel active={sort === "units"} direction={sort === "units" ? order : "desc"} onClick={() => changeSort("units")}>Credits used</TableSortLabel></TableCell>
          </TableRow></TableHead>
          <TableBody>
            {!loading && result?.rows.map(row => <TableRow key={row.customer_id ?? "unassigned"} hover>
              <TableCell><Link href="#credit-history" onClick={() => onSelect({ id: row.customer_id == null ? "unassigned" : String(row.customer_id), name: row.name || "Unassigned" }, periodId)}>{row.name || "Unassigned"}</Link></TableCell>
              {[row.chat_units, row.automatic_ticket_units, row.technician_ticket_units, row.workflow_units, row.units].map((units, i) => <TableCell key={i} align="right">{credits(units)}</TableCell>)}
            </TableRow>)}
            {(loading || !result?.rows.length) && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}>
              {loading ? "Loading client usage…" : error ? "Client usage is unavailable." : "No client usage matches these filters."}
            </TableCell></TableRow>}
          </TableBody>
        </Table></Box>
        <TablePagination component="div" labelRowsPerPage="Per page" sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap", px: 0 }, "& .MuiTablePagination-actions": { ml: 1 } }} count={loading || error ? -1 : result?.total || 0} page={page} rowsPerPage={size} rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
          backIconButtonProps={{ disabled: loading || page === 0 }} nextIconButtonProps={{ disabled: loading || (page + 1) * size >= (result?.total || 0) }} />
      </Stack>
    </AccordionDetails>
  </Accordion>;
}
